/**
 * Preserves the route a signed-out user tried to reach before being
 * bounced through sign-in (YAR-267 AC11 — "deep-link auth redirect").
 *
 * React Router's in-memory `location.state` survives client-side
 * navigations, but NOT the Google OAuth round trip: `signInWithGoogle`
 * does a real `window.location` navigation away to Google and back, which
 * remounts the SPA fresh with no history/state carried over. sessionStorage
 * is the only thing that survives that trip, so it's the source of truth —
 * `ProtectedRoute` stashes here rather than in router state.
 *
 * One-shot by design: `clearPostAuthRedirect` must be called once the
 * redirect has actually been honored, otherwise a stale target could
 * hijack an unrelated later sign-in in the same tab.
 */

const STORAGE_KEY = 'yaruta_post_auth_redirect';

/** Reject anything but an in-app path — never redirect to an external URL. */
function isSafeRedirectTarget(path: string): boolean {
  // Backslashes are rejected too: browsers normalize `\` to `/`, so a
  // target like `/\evil.com` or `\\evil.com` would resolve to a
  // protocol-relative external URL despite passing the `//` check.
  return path.startsWith('/') && !path.startsWith('//') && !path.includes('\\');
}

export function savePostAuthRedirect(path: string): void {
  if (!isSafeRedirectTarget(path)) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, path);
  } catch {
    /* ignore — sessionStorage unavailable (private mode, etc.) */
  }
}

/** Read-only — safe to call from render. Does not clear the stashed value. */
export function peekPostAuthRedirect(): string | null {
  try {
    const value = window.sessionStorage.getItem(STORAGE_KEY);
    return value && isSafeRedirectTarget(value) ? value : null;
  } catch {
    return null;
  }
}

/** Call once the stashed redirect has been consumed (e.g. from an effect). */
export function clearPostAuthRedirect(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore — sessionStorage unavailable */
  }
}
